import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChartLine, TrendUp, Users, Hash, ArrowsClockwise, CircleNotch, Sparkle } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function CommentsInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/comments/insights");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="text-sm py-12 text-center" style={{ color: "var(--ink-3)" }}><CircleNotch size={16} className="animate-spin inline mr-2"/> Analyzing your comments…</div>;
  }
  if (!data || data.total === 0) {
    return (
      <div className="card p-16 text-center" data-testid="insights-empty">
        <ChartLine size={26} className="mx-auto mb-3" style={{ color: "var(--accent)" }}/>
        <div className="eyebrow mb-2">Nothing to analyze yet</div>
        <p className="font-serif text-3xl mb-3">Log 3+ comments to unlock insights.</p>
        <p className="text-sm max-w-md mx-auto" style={{ color: "var(--ink-2)" }}>
          She'll cluster your themes, surface the creators you engage with most, and show your weekly cadence.
        </p>
      </div>
    );
  }

  const funnel = data.funnel || { logged: 0, reactions: 0, replies: 0, connections: 0 };
  const weekly = (data.weekly_cadence || []).map((w) => ({
    week: w.week.split("-W")[1] ? `W${w.week.split("-W")[1]}` : w.week,
    Comments: w.count,
    Reactions: w.reactions,
  }));

  return (
    <div className="space-y-6" data-testid="insights-root">
      {/* Funnel */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Comments logged", val: funnel.logged,      hint: "" },
          { label: "Reactions earned", val: funnel.reactions,  hint: funnel.logged ? `${(funnel.reactions / funnel.logged).toFixed(1)} avg per comment` : "" },
          { label: "Replies to you",   val: funnel.replies,    hint: funnel.logged ? `${((funnel.replies / funnel.logged) * 100).toFixed(0)}% reply rate` : "" },
          { label: "New connections",  val: funnel.connections,hint: funnel.logged ? `${((funnel.connections / funnel.logged) * 100).toFixed(0)}% conversion` : "" },
        ].map((k) => (
          <div key={k.label} className="card p-5 fade-up" data-testid={`funnel-${k.label.toLowerCase().replace(/[^a-z]/g,'')}`}>
            <div className="eyebrow mb-2">{k.label}</div>
            <div className="font-serif text-3xl tracking-tight font-semibold">{Number(k.val || 0).toLocaleString()}</div>
            {k.hint && <div className="text-[11px] font-mono mt-1" style={{ color: "var(--ink-3)" }}>{k.hint}</div>}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Top themes */}
        <div className="card p-5 fade-up lg:col-span-2" data-testid="insights-themes">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow mb-1 flex items-center gap-1.5" style={{ color: "var(--accent-2)" }}>
                <Sparkle size={12} weight="fill"/> AI · Top themes
              </div>
              <div className="text-sm" style={{ color: "var(--ink-2)" }}>Patterns she found across every comment you've logged.</div>
            </div>
            <button
              onClick={load}
              data-testid="insights-refresh"
              className="p-2 rounded-full hover:bg-[color:var(--paper)]"
              title="Re-analyze"
              aria-label="Refresh insights"
            ><ArrowsClockwise size={14}/></button>
          </div>
          {(data.top_themes || []).length === 0 ? (
            <div className="text-sm py-4" style={{ color: "var(--ink-3)" }}>No clear themes yet. Log more comments and re-analyze.</div>
          ) : (
            <div className="space-y-3">
              {data.top_themes.map((t, i) => {
                const pct = data.total ? Math.min(100, Math.round((t.count / data.total) * 100)) : 0;
                return (
                  <div key={i} data-testid={`theme-${i}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Hash size={11} style={{ color: "var(--accent)" }}/>
                        <span className="text-sm font-medium">{t.label}</span>
                      </div>
                      <span className="text-xs font-mono" style={{ color: "var(--ink-3)" }}>{t.count} · {pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--line)" }}>
                      <div style={{ width: `${pct}%`, background: "var(--accent)", height: "100%", transition: "width 400ms ease" }}/>
                    </div>
                    {t.examples?.length > 0 && (
                      <div className="mt-1.5 text-[11px] italic" style={{ color: "var(--ink-3)" }}>
                        "{t.examples[0].snippet}"
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top authors */}
        <div className="card p-5 fade-up" data-testid="insights-authors">
          <div className="eyebrow mb-1 flex items-center gap-1.5"><Users size={12} weight="fill" style={{ color: "var(--accent)" }}/> Top creators</div>
          <div className="text-sm mb-3" style={{ color: "var(--ink-2)" }}>Where you show up most.</div>
          {(data.top_authors || []).length === 0 ? (
            <div className="text-sm" style={{ color: "var(--ink-3)" }}>Add author names when you log a comment to see this ranking.</div>
          ) : (
            <div className="space-y-2">
              {data.top_authors.map((a, i) => (
                <div key={a.author + i} data-testid={`author-${i}`} className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{a.author}</div>
                    <div className="text-[11px] font-mono" style={{ color: "var(--ink-3)" }}>{a.reactions} react · {a.replies} replies · {a.connections} conn</div>
                  </div>
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full" style={{ background: "var(--accent-tint)", color: "var(--accent-2)" }}>×{a.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Weekly cadence */}
      <div className="card p-5 fade-up" data-testid="insights-cadence">
        <div className="eyebrow mb-1 flex items-center gap-1.5"><TrendUp size={12} weight="fill" style={{ color: "var(--accent)" }}/> Weekly cadence</div>
        <div className="text-sm mb-4" style={{ color: "var(--ink-2)" }}>Last 12 weeks of comment volume and reactions earned.</div>
        {weekly.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--ink-3)" }}>No weekly data yet.</div>
        ) : (
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={weekly} margin={{ top: 8, right: 4, left: -20, bottom: 4 }}>
                <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false}/>
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "var(--ink-3)" }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 11, fill: "var(--ink-3)" }} axisLine={false} tickLine={false}/>
                <Tooltip cursor={{ fill: "var(--bg-soft)" }} contentStyle={{ background: "var(--bg-elev)", border: "1px solid var(--line-2)", borderRadius: 12, fontSize: 12 }}/>
                <Bar dataKey="Comments" fill="var(--accent)" radius={[8, 8, 0, 0]}/>
                <Bar dataKey="Reactions" fill="#FFA5BC" radius={[8, 8, 0, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
